import { parsePaymentErrorPath } from '@src/domains/payments';

import type { CardFormVisibility } from './resolveCardFormVisibility';

export type CardFormFieldError = {
  path: string;
  message: string;
};

const VALIDITY_PAYLOAD_PATHS = ['validityMonth', 'validityYear'];

const WHOLE_CARD_FIELD = 'cardNumber';

const ALWAYS_RENDERED_FIELDS = ['cardNumber', 'holder', 'validity', 'securityCode', 'billingAddress.zipCode'];

const isRendered = (path: string, visibility: CardFormVisibility): boolean => {
  if (ALWAYS_RENDERED_FIELDS.includes(path)) {
    return true;
  }

  if (path === 'holderIdentity') {
    return visibility.showHolderIdentity;
  }

  if (path === 'installments') {
    return visibility.showInstallments;
  }

  if (path.startsWith('billingAddress.')) {
    return visibility.showBillingAddress;
  }

  return false;
};

const resolveFieldPath = (payloadPath: string, visibility: CardFormVisibility): string => {
  if (payloadPath === '') {
    return WHOLE_CARD_FIELD;
  }

  const path = VALIDITY_PAYLOAD_PATHS.includes(payloadPath) ? 'validity' : payloadPath;

  return isRendered(path, visibility) ? path : WHOLE_CARD_FIELD;
};

/**
 * Turns the flat `{ path: messages[] }` map the store holds after a 422 into react-hook-form field
 * errors, folded onto the fields this form state actually renders.
 */
export const mapPaymentErrorsToFormFields = (
  errors: { [key: string]: string[] },
  visibility: CardFormVisibility,
): CardFormFieldError[] => {
  const byField = new Map<string, string[]>();

  Object.entries(errors).forEach(([key, messages]) => {
    const payloadPath = parsePaymentErrorPath(key);

    if (payloadPath === null || !messages?.length) {
      return;
    }

    const path = resolveFieldPath(payloadPath, visibility);

    byField.set(path, [...(byField.get(path) ?? []), ...messages]);
  });

  return Array.from(byField, ([path, messages]) => ({ path, message: messages.join(', ') }));
};
